import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Form, Button, Row, Col, Alert, Badge, Modal } from "react-bootstrap";
import * as VtermActions from "../../Actions/VirtualTerminalActions/actions";
import Loader from "../Loader/Loader";
import VtermResultModal from "./VtermResultModal";
import InputMask from "react-input-mask";
import { numberCheck as luhnCheck, validateCardExpiry } from "../helper";

const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const OTHER_PRODUCT_VALUE = "__other__";

function VirtualTerminal() {
  const dispatch = useDispatch();
  const { config, products, tenants, unitToken, loading } = useSelector((state) => state.VirtualTerminalDetails);
  const { user_type: currentUserType, suite_id: currentSuiteId, id: currentUserId, email: currentUserEmail } = useSelector(
    (state) => state.UserProfileDetails || {},
  );
  const isUnitUser = currentUserType === "unit";

  const [selectedProduct, setSelectedProduct] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardCvv, setCardCvv] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [expiryError, setExpiryError] = useState("");
  const [cardNumberError, setCardNumberError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [editingCard, setEditingCard] = useState(false);
  const [customProductName, setCustomProductName] = useState("");
  const [customProductPrice, setCustomProductPrice] = useState("");
  const [customProductType, setCustomProductType] = useState("");

  // Load config on mount
  useEffect(() => {
    dispatch(VtermActions.getVtermConfig());
  }, [dispatch]);

  // When tenant changes, fetch saved card and reset form
  useEffect(() => {
    if (selectedTenant) {
      const tenant = tenants.find((t) => String(t.id) === String(selectedTenant));
      if (tenant) {
        dispatch(VtermActions.getUnitToken(tenant.suite_id));
        setEmail(tenant.email || "");
      }
    } else {
      setEmail(isUnitUser ? currentUserEmail || "" : "");
    }
    setCardName("");
    setCardNumber("");
    setCardExpiry("");
    setCardCvv("");
    setSaveCard(false);
    setCardNumberError("");
    setExpiryError("");
    setEmailError("");
    setEditingCard(false);
  }, [selectedTenant, tenants, dispatch, isUnitUser, currentUserEmail]);

  useEffect(() => {
    if (!isUnitUser || !currentSuiteId || !tenants?.length) return;
    const tenant = tenants.find((t) => t.suite_id === currentSuiteId) || tenants.find((t) => String(t.id) === String(currentUserId));
    if (tenant && String(selectedTenant) !== String(tenant.id)) {
      setSelectedTenant(String(tenant.id));
    }
  }, [isUnitUser, currentSuiteId, currentUserId, tenants, selectedTenant]);

  const getSelectedProduct = useCallback(() => {
    if (selectedProduct === OTHER_PRODUCT_VALUE) {
      return {
        id: null,
        description: customProductName.trim(),
        amount: customProductPrice,
      };
    }
    return products.find((p) => p.id === parseInt(selectedProduct, 10));
  }, [products, selectedProduct, customProductName, customProductPrice]);

  const isOtherProductSelected = selectedProduct === OTHER_PRODUCT_VALUE;
  const isCustomProductNameValid = customProductName.trim().length > 0;
  const customPriceNumber = parseFloat(customProductPrice);
  const isCustomProductPriceValid = !Number.isNaN(customPriceNumber) && customPriceNumber > 0;
  const isCustomProductTypeValid = customProductType === "permit" || customProductType === "validation";
  const isCustomProductValid = !isOtherProductSelected || (isCustomProductNameValid && isCustomProductPriceValid && isCustomProductTypeValid);

  const getTotalAmount = useCallback(() => {
    const product = getSelectedProduct();
    if (!product) return "0.00";
    return (parseFloat(product.amount) * quantity).toFixed(2);
  }, [getSelectedProduct, quantity]);

  const getSelectedTenant = useCallback(() => {
    return (
      tenants.find((t) => String(t.id) === String(selectedTenant)) ??
      (isUnitUser && currentSuiteId
        ? { id: currentUserId || null, suite_id: currentSuiteId, email: currentUserEmail || "" }
        : null)
    );
  }, [tenants, selectedTenant, isUnitUser, currentSuiteId, currentUserId, currentUserEmail]);

  const handleSubmit = (e) => {
    e.preventDefault();
  };

  const validateEmail = (value) => {
    const normalizedEmail = typeof value === "string" ? value.trim() : "";
    if (!normalizedEmail) return "Email is required";
    if (!EMAIL_REGEX.test(normalizedEmail)) return "Please enter a valid email";
    return "";
  };

  const handleCharge = () => {
    const tenant = getSelectedTenant();
    if (!selectedProduct || !tenant || quantity < 1) return;
    if (!isCustomProductValid) return;

    const emailErr = validateEmail(email);
    if (emailErr) {
      setEmailError(emailErr);
      return;
    }
    setEmailError("");

    if (unitToken && !editingCard) return setShowConfirm(true);
    if (!cardNumber || !cardExpiry || !cardCvv || !cardName) return;

    const cardErr = validateCardNumber(cardNumber);
    if (cardErr) return setCardNumberError(cardErr);
    setCardNumberError("");

    const expiryErr = validateCardExpiry(cardExpiry);
    if (expiryErr) return setExpiryError(expiryErr);
    setExpiryError("");

    setShowConfirm(true);
  };

  const validateCardNumber = (number) => {
    if (!/^\d{13,19}$/.test(number)) {
      return "Please enter a valid card number";
    }
    if (!luhnCheck(number)) {
      return "Please enter a valid card number";
    }
    return "";
  };

  const confirmSale = async () => {
    const useExisting = unitToken && !editingCard;
    if (!isCustomProductValid) {
      setShowConfirm(false);
      return;
    }
    if (!useExisting) {
      const error = validateCardExpiry(cardExpiry);
      if (error) {
        setExpiryError(error);
        setShowConfirm(false);
        return;
      }
    }
    setShowConfirm(false);
    setProcessing(true);

    const tenant = getSelectedTenant();
    const body = {
      product_id: isOtherProductSelected ? undefined : parseInt(selectedProduct, 10),
      custom_product_name: isOtherProductSelected ? customProductName.trim() : undefined,
      custom_product_price: isOtherProductSelected ? customPriceNumber.toFixed(2) : undefined,
      custom_product_type: isOtherProductSelected ? customProductType : undefined,
      quantity,
      suite_id: tenant?.suite_id,
      user_id: tenant?.id,
      note: note || undefined,
      email: normalizedEmail,
      use_saved_card: useExisting,
      save_card: !useExisting ? saveCard : undefined,
      card_details: useExisting ? undefined : {
        cc_number: cardNumber,
        card_expiry: cardExpiry,
        cvv: cardCvv,
        name: cardName,
      },
    };

    const result = await dispatch(VtermActions.processSale(body));
    setProcessing(false);
    setLastResult(result);

    if (result?.success === 1) {
      // Reset form
      setSelectedProduct("");
      setCustomProductName("");
      setCustomProductPrice("");
      setCustomProductType("");
      setQuantity(1);
      setSelectedTenant("");
      setEmail("");
      setNote("");
      setCardNumber("");
      setCardExpiry("");
      setCardCvv("");
      setCardName("");
      setSaveCard(false);
      setEditingCard(false);
      // Refresh token info
      if (tenant?.suite_id) {
        dispatch(VtermActions.getUnitToken(tenant.suite_id));
      }
      dispatch(VtermActions.getVtermConfig());
    }
  };

  const handleDeleteSavedCard = () => {
    const tenant = getSelectedTenant();
    if (tenant) {
      dispatch(VtermActions.deleteUnitToken(tenant.suite_id));
    }
  };

  const handleCreateProduct = async () => {
    if (!isOtherProductSelected || !isCustomProductValid) return;

    const body = {
      custom_product_name: customProductName.trim(),
      custom_product_price: customPriceNumber.toFixed(2),
      custom_product_type: customProductType,
    };

    const result = await dispatch(VtermActions.createCustomProduct(body));
    if (result?.success === 1) {
      await dispatch(VtermActions.getVtermConfig());
      if (result?.data?.id) {
        setSelectedProduct(String(result.data.id));
      }
      setCustomProductName("");
      setCustomProductPrice("");
      setCustomProductType("");
    }
  };

  // Not configured
  if (!loading && !config) {
    return (
      <Card>
        <Card.Header>
          <Card.Title as="h4">Virtual Terminal</Card.Title>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">Virtual Terminal is not configured for this property.</Alert>
        </Card.Body>
      </Card>
    );
  }

  const product = getSelectedProduct();
  const tenant = getSelectedTenant();
  const normalizedEmail = typeof email === "string" ? email.trim() : "";
  const isEmailValid = !!normalizedEmail && EMAIL_REGEX.test(normalizedEmail);

  return (
    <>
      <div style={{ position: "relative" }}>
        {processing && (
          <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(255,255,255,0.7)", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Loader />
          </div>
        )}

      <VtermResultModal result={lastResult} onClose={() => setLastResult(null)} />

      <Card>
        <Card.Header>
          <Card.Title as="h4">Virtual Terminal</Card.Title>
          <p className="card-category">Sell prepaid validation products</p>
        </Card.Header>
        <Card.Body>

          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product</Form.Label>
                  <Form.Control as="select" value={selectedProduct} onChange={(e) => setSelectedProduct(e.target.value)} required>
                    <option value="">Select a product...</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.description} — ${parseFloat(p.amount).toFixed(2)}
                      </option>
                    ))}
                    <option value={OTHER_PRODUCT_VALUE}>Other</option>
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Quantity</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="999"
                    value={quantity === null ? '' : quantity}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") {
                        setQuantity("");
                      } else {
                        const num = parseInt(val, 10);
                        if (!isNaN(num)) setQuantity(num);
                      }
                    }}
                    onBlur={(e) => {
                      let val = parseInt(e.target.value, 10);
                      if (isNaN(val) || val < 1) val = 1;
                      if (val > 999) val = 999;
                      setQuantity(val);
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Total</Form.Label>
                  <div className="form-control-plaintext" style={{ fontSize: "1.2em", fontWeight: "bold" }}>
                    ${getTotalAmount()}
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {isOtherProductSelected && (
              <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Custom Product Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={customProductName}
                      onChange={(e) => setCustomProductName(e.target.value)}
                      placeholder="Enter product name"
                      maxLength={100}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Custom Unit Price</Form.Label>
                    <Form.Control
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={customProductPrice}
                      onChange={(e) => setCustomProductPrice(e.target.value)}
                      placeholder="0.00"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type</Form.Label>
                    <Form.Control
                      as="select"
                      value={customProductType}
                      onChange={(e) => setCustomProductType(e.target.value)}
                      required
                    >
                      <option value="">Select type...</option>
                      <option value="permit">Permit</option>
                      <option value="validation">Validation</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <Button
                    variant="outline-primary"
                    type="button"
                    onClick={handleCreateProduct}
                    disabled={!isCustomProductValid}
                  >
                    Create Product
                  </Button>
                </Col>
              </Row>
              </>
            )}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Unit/Tenant</Form.Label>
                  {isUnitUser ? (
                    <Form.Control type="text" value={tenant?.suite_id || currentSuiteId || "-"} readOnly disabled />
                  ) : (
                    <Form.Control as="select" value={selectedTenant} onChange={(e) => setSelectedTenant(e.target.value)} required>
                      <option value="">Select a unit...</option>
                      {tenants.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.suite_id}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email (for receipt)<span className="asterisk">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) setEmailError("");
                    }}
                    onBlur={(e) => setEmailError(validateEmail(e.target.value))}
                    placeholder="Enter receipt email"
                    isInvalid={!!emailError}
                    required
                  />
                  <Form.Text className="text-muted">Receipt will be sent to this email address.</Form.Text>
                  {emailError && <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback>}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Note (optional)</Form.Label>
                  <Form.Control type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} placeholder="Optional operator note" />
                </Form.Group>
              </Col>
            </Row>

            {/* Saved Card Section */}
            {selectedTenant && unitToken && (
              <div className="mb-3 mt-3 change-credit-card-box">
                <div className="mt-2">
                  {!editingCard ? (
                    <Row>
                      <Col lg={6}>
                        <div className="form-group mb-3">
                          <label>Card Holder Name</label>
                          <div className="d-flex align-items-center position-relative">
                            <input type="text" className="form-control pe-5" value={unitToken.name || ""} disabled readOnly />
                            <div className="payment-formicon"><span className="fa fa-user" aria-hidden="true" /></div>
                          </div>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form-group mb-3">
                          <label>Card Number</label>
                          <div className="d-flex align-items-center position-relative">
                            <input type="text" className="form-control pe-5" value={unitToken.masked_card || ""} disabled readOnly />
                            <div className="payment-formicon"><span className="fa fa-credit-card" aria-hidden="true" /></div>
                          </div>
                        </div>
                      </Col>
                      <Col lg={3}>
                        <div className="form-group mb-3">
                          <label>Card Type</label>
                          <div className="d-flex align-items-center position-relative">
                            <input type="text" className="form-control" value={unitToken.card_type || ""} disabled readOnly />
                          </div>
                        </div>
                      </Col>
                      <Col lg={3}>
                        <div className="form-group mb-3">
                          <label>Expiry</label>
                          <div className="d-flex align-items-center position-relative">
                            <input type="text" className="form-control" value={unitToken.expiry_date || ""} disabled readOnly />
                            <div className="payment-formicon"><span className="fa fa-calendar" aria-hidden="true" /></div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Row>
                        <Col lg={6}>
                          <div className="form-group mb-3">
                            <label>Card Holder Name<span className="asterisk">*</span></label>
                            <div className="d-flex align-items-center position-relative">
                              <input type="text" className="form-control pe-5" value={cardName} onChange={(e) => setCardName(e.target.value)} placeholder="Card Holder Name" required />
                              <div className="payment-formicon"><span className="fa fa-user" aria-hidden="true" /></div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col lg={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Card Number<span className="asterisk">*</span></Form.Label>
                            <div className="d-flex align-items-center position-relative">
                              <Form.Control
                                type="text" value={cardNumber}
                                onChange={(e) => { setCardNumber(e.target.value.replace(/[^0-9]/g, "")); if (cardNumberError) setCardNumberError(""); }}
                                placeholder={unitToken?.masked_card || "Card Number"} maxLength={19} isInvalid={!!cardNumberError} required
                              />
                              {cardNumberError && <Form.Control.Feedback type="invalid">{cardNumberError}</Form.Control.Feedback>}
                              <div style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", fontSize: "19px", color: "#5962a9", opacity: 0.5, pointerEvents: "none" }}>
                                <span className="fa fa-credit-card" aria-hidden="true" />
                              </div>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col lg={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Expiration<span className="asterisk">*</span></Form.Label>
                            <div className="d-flex align-items-center position-relative">
                              <InputMask mask="99/99" maskChar={null} value={cardExpiry} onChange={(e) => { setCardExpiry(e.target.value); if (expiryError) setExpiryError(""); }}>
                                {(inputProps) => <Form.Control {...inputProps} type="text" placeholder="MM/YY" isInvalid={!!expiryError} required />}
                              </InputMask>
                              {expiryError && <Form.Control.Feedback type="invalid">{expiryError}</Form.Control.Feedback>}
                              <div style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", fontSize: "19px", color: "#5962a9", opacity: 0.5, pointerEvents: "none" }}>
                                <span className="fa fa-calendar" aria-hidden="true" />
                              </div>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col lg={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>CVV<span className="asterisk">*</span></Form.Label>
                            <div className="d-flex align-items-center position-relative">
                              <Form.Control type="password" value={cardCvv} onChange={(e) => setCardCvv(e.target.value.replace(/[^0-9]/g, ""))} placeholder="CVV" maxLength={4} required />
                              <div style={{ position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)", fontSize: "19px", color: "#5962a9", opacity: 0.5, pointerEvents: "none" }}>
                                <span className="fa fa-key" aria-hidden="true" />
                              </div>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                  <div className="d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
                    {editingCard ? (
                      <Button variant="outline-secondary" size="sm" onClick={() => setEditingCard(false)}>
                        <i className="fas fa-times mr-1"></i> Cancel Edit
                      </Button>
                    ) : (
                      <Button variant="outline-primary" size="sm" onClick={() => {
                        // Only the first 6 and last 4 digits are stored — so card number must be re-entered for security reasons (PCI compliance).
                        // Card Holder Name and Expiry are fully pre-filled since those are stored in plain text.
                        setCardName(unitToken.name || "");
                        setCardExpiry(unitToken.expiry_date || "");
                        setEditingCard(true);
                      }}>
                        <i className="fas fa-edit mr-1"></i> Edit Card
                      </Button>
                    )}
                    <Button variant="outline-danger" size="sm" onClick={handleDeleteSavedCard}>
                      <i className="far fa-trash-alt mr-1"></i>Delete Card
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Card Details Fields — only when no saved card */}
            {selectedTenant && !unitToken && (
              <div
                className="mb-3 mt-3"
                style={{
                  border: "1px solid #dbd8d8",
                  padding: "10px",
                  background: "#f7f7f7",
                  borderRadius: "5px",
                }}
              >
                <div className="mt-2">
                  <Row>
                    <Col lg={6}>
                      <div className="form-group mb-3">
                        <label>
                          Card Holder Name<span className="asterisk">*</span>
                        </label>
                        <div className="d-flex align-items-center position-relative">
                          <input
                            type="text"
                            className="form-control pe-5"
                            value={cardName}
                            onChange={(e) => setCardName(e.target.value)}
                            placeholder="Card Holder Name"
                            required
                          />
                          <div className="payment-formicon">
                            <span className="fa fa-user" aria-hidden="true" />
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Card Number<span className="asterisk">*</span>
                        </Form.Label>
                        <div className="d-flex align-items-center position-relative">
                          <Form.Control
                            type="text"
                            value={cardNumber}
                            onChange={(e) => {
                              setCardNumber(e.target.value.replace(/[^0-9]/g, ""));
                              if (cardNumberError) setCardNumberError("");
                            }}
                            placeholder="Card Number"
                            maxLength={19}
                            isInvalid={!!cardNumberError}
                            required
                          />
                          {cardNumberError && (
                            <Form.Control.Feedback type="invalid">
                              {cardNumberError}
                            </Form.Control.Feedback>
                          )}
                          <div
                            style={{
                              position: "absolute",
                              right: "16px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "19px",
                              color: "#5962a9",
                              opacity: 0.5,
                              pointerEvents: "none",
                            }}
                          >
                            <span className="fa fa-credit-card" aria-hidden="true" />
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Expiration<span className="asterisk">*</span>
                        </Form.Label>
                        <div className="d-flex align-items-center position-relative">
                          <InputMask
                            mask="99/99"
                            maskChar={null}
                            value={cardExpiry}
                            onChange={(e) => {
                              setCardExpiry(e.target.value);
                              if (expiryError) setExpiryError("");
                            }}
                          >
                            {(inputProps) => (
                              <Form.Control
                                {...inputProps}
                                type="text"
                                placeholder="MM/YY"
                                isInvalid={!!expiryError}
                                required
                              />
                            )}
                          </InputMask>
                          {expiryError && (
                            <Form.Control.Feedback type="invalid">
                              {expiryError}
                            </Form.Control.Feedback>
                          )}
                          <div
                            style={{
                              position: "absolute",
                              right: "16px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "19px",
                              color: "#5962a9",
                              opacity: 0.5,
                              pointerEvents: "none",
                            }}
                          >
                            <span className="fa fa-calendar" aria-hidden="true" />
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col lg={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          CVV<span className="asterisk">*</span>
                        </Form.Label>
                        <div className="d-flex align-items-center position-relative">
                          <Form.Control
                            type="password"
                            value={cardCvv}
                            onChange={(e) => setCardCvv(e.target.value.replace(/[^0-9]/g, ""))}
                            placeholder="CVV"
                            maxLength={4}
                            required
                          />
                          <div
                            style={{
                              position: "absolute",
                              right: "16px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              fontSize: "19px",
                              color: "#5962a9",
                              opacity: 0.5,
                              pointerEvents: "none",
                            }}
                          >
                            <span className="fa fa-key" aria-hidden="true" />
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </div>
            )}

            {/* Save Card Checkbox — only shown when entering a new card */}
            {selectedTenant && !unitToken && (
              <Form.Group className="mb-3" controlId="saveCardCheckbox">
                <label className="fancy-checkbox">
                  <input
                    type="checkbox"
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                  />
                  <span>
                    <i></i>Save card for this unit
                  </span>
                </label>
              </Form.Group>
            )}

            <Button
              variant="primary"
              type="button"
              onClick={handleCharge}
              disabled={loading || !selectedProduct || !tenant || quantity < 1 || !isEmailValid || !isCustomProductValid || ((!unitToken || editingCard) && (!cardNumber || !!cardNumberError || !cardExpiry || cardExpiry.replace(/\//g, "").length !== 4 || !!expiryError || !cardCvv || !cardName))}
              size="lg"
              className="w-100"
            >
              {`Charge $${getTotalAmount()}`}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Confirmation Modal */}
      <Modal lg="medium" show={showConfirm} onHide={() => setShowConfirm(false)}>
        <Modal.Header className="no-border">
          <h4 className="m-0 font-weight-bold">Confirm Sale</h4>
          <Button
            title="Cancel"
            type="button"
            onClick={() => setShowConfirm(false)}
            className="close-btn"
            data-dismiss="modal"
          >
            <i className="nc-icon nc-simple-remove"></i>
          </Button>
        </Modal.Header>
        <Modal.Body className="modal-data-body">
          <div style={{ lineHeight: "2" }}>
            <p className="mb-1">
              <span className="text-muted">Product:</span>{" "}
              <strong>{product?.description}</strong>
            </p>
            <p className="mb-1">
              <span className="text-muted">Unit:</span>{" "}
              <strong>{tenant?.suite_id}</strong>
            </p>
            <p className="mb-1">
              <span className="text-muted">Qty × Price:</span>{" "}
              <strong>
                {quantity} × ${product && !Number.isNaN(parseFloat(product.amount)) ? parseFloat(product.amount).toFixed(2) : "0.00"}
              </strong>
            </p>
            <p className="mb-1">
              <span className="text-muted">Total:</span>{" "}
              <strong>${getTotalAmount()}</strong>
            </p>
            <p className="mb-1">
              <span className="text-muted">Payment:</span>{" "}
              {unitToken && !editingCard ? (
                <Badge bg="info">
                  {unitToken?.card_type ? `${unitToken.card_type} — ` : ""}{unitToken?.masked_card}
                </Badge>
              ) : (
                <Badge bg="secondary">New Card</Badge>
              )}
            </p>
            {normalizedEmail && (
              <p className="mb-1">
                <span className="text-muted">Receipt to:</span> {normalizedEmail}
              </p>
            )}
            {note && (
              <p className="mb-1">
                <span className="text-muted">Note:</span> {note}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowConfirm(false)} type="button" className="btn btn-danger">
            Cancel
          </Button>
          <Button
            style={{ minWidth: "93px" }}
            onClick={confirmSale}
            type="button"
            className="btn btn-fill btn-info"
          >
            Confirm & Charge
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </>
  );
}

export default VirtualTerminal;
